"use client";

import { Check, ChevronDown, Globe, Search } from "lucide-react";
import * as React from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useLanguage } from "@/contexts/LanguageContext";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { type LanguageMetadata, SUPPORTED_LANGUAGES } from "@/lib/languages";
import { reportClientError } from "@/lib/observability/report-client-error";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Region helpers                                                     */
/* ------------------------------------------------------------------ */

const POPULAR_CODES = new Set([
  "en",
  "es",
  "fr",
  "de",
  "pt",
  "zh",
  "ja",
  "ko",
  "ar",
  "hi",
  "ru",
]);

const ASIA_CODES = new Set([
  "zh",
  "ja",
  "ko",
  "vi",
  "th",
  "id",
  "ms",
  "tl",
  "fil",
  "hi",
  "bn",
  "ta",
  "te",
  "kn",
  "ml",
  "mr",
  "gu",
  "pa",
  "ne",
  "si",
  "my",
  "km",
  "lo",
  "mn",
  "jv",
  "su",
  "or",
]);

const MIDDLE_EAST_CODES = new Set([
  "ar",
  "fa",
  "he",
  "tr",
  "ur",
  "ps",
  "ku",
  "sd",
]);
const AFRICA_CODES = new Set(["sw", "af", "zu", "am", "ha", "ig", "yo", "so"]);

type RegionKey =
  | "popular"
  | "europe"
  | "asia"
  | "middleEast"
  | "africa"
  | "other";

function getRegion(code: string): RegionKey {
  if (POPULAR_CODES.has(code)) return "popular";
  if (ASIA_CODES.has(code)) return "asia";
  if (MIDDLE_EAST_CODES.has(code)) return "middleEast";
  if (AFRICA_CODES.has(code)) return "africa";
  return "europe";
}

const REGION_ORDER: RegionKey[] = [
  "popular",
  "europe",
  "asia",
  "middleEast",
  "africa",
  "other",
];

const REGION_LABELS: Record<RegionKey, string> = {
  popular: "groups.recent",
  europe: "groups.europe",
  asia: "groups.asia",
  middleEast: "groups.middleEast",
  africa: "groups.africa",
  other: "groups.oceania",
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface LanguageSwitcherProps {
  compact?: boolean;
  className?: string;
}

export function LanguageSwitcher({
  compact = false,
  className,
}: LanguageSwitcherProps) {
  const { t } = useJsonTranslation("LanguageSwitcher");
  const { language, setLanguage, getFlag, getNativeName, availableLanguages } =
    useLanguage();
  const [search, setSearch] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const searchRef = React.useRef<HTMLInputElement>(null);

  // Focus search on open
  React.useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchRef.current?.focus());
    } else {
      setSearch("");
    }
  }, [isOpen]);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return availableLanguages;
    const q = search.toLowerCase();
    return availableLanguages.filter(
      (l) =>
        l.code.toLowerCase().includes(q) ||
        l.name.toLowerCase().includes(q) ||
        l.nativeName.toLowerCase().includes(q),
    );
  }, [availableLanguages, search]);

  const grouped = React.useMemo(() => {
    const map = new Map<RegionKey, LanguageMetadata[]>();
    for (const region of REGION_ORDER) map.set(region, []);
    for (const lang of filtered) {
      const region = getRegion(lang.code);
      map.get(region)!.push(lang);
    }
    return map;
  }, [filtered]);

  const handleSelect = async (code: string) => {
    try {
      await setLanguage(code as (typeof SUPPORTED_LANGUAGES)[number]);
      setIsOpen(false);
    } catch (error) {
      reportClientError(error, {
        source: "components.LanguageSwitcher",
        feature: "i18n",
        action: "handleSelect",
        extra: { languageCode: code },
      });
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("ariaLabel")}
          className={cn(
            "inline-flex items-center justify-center transition-all duration-200",
            compact
              ? "text-muted-foreground hover:text-foreground h-9 w-9 rounded-full hover:bg-white/[0.06]"
              : "border-border gap-2 rounded-xl border px-3 py-2 text-sm hover:bg-white/[0.06]",
            className,
          )}
        >
          {compact ? (
            <Globe className="h-4 w-4" />
          ) : (
            <>
              <span className="text-base leading-none">{getFlag()}</span>
              <span className="text-foreground text-sm font-medium">
                {getNativeName()}
              </span>
              <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
            </>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="from-surface-raised/95 to-surface-raised/80 w-[min(320px,calc(100vw-2.5rem))] overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b p-0 shadow-2xl backdrop-blur-xl sm:w-[360px]"
      >
        {/* Header */}
        <div className="border-b border-white/[0.06] px-4 pt-4 pb-3">
          <div className="mb-3 flex items-center gap-2">
            <div className="from-leaf-500/20 to-leaf-500/20 flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br ring-1 ring-white/[0.08]">
              <Globe className="text-leaf-400 h-4 w-4" />
            </div>
            <div>
              <h3 className="text-foreground text-sm font-semibold">
                {t("title") === "title" ? "Language" : t("title")}
              </h3>
              <p className="text-muted-foreground/60 text-[11px]">
                {t("availableLanguages", {
                  count: SUPPORTED_LANGUAGES.length,
                })}
              </p>
            </div>
          </div>
          {/* Search */}
          <div className="relative">
            <Search className="text-muted-foreground/50 absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
            <input
              ref={searchRef}
              type="text"
              placeholder={t("searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-border/50 bg-background/50 placeholder:text-muted-foreground/40 focus:border-leaf-500/50 focus:ring-leaf-500/20 h-9 w-full rounded-lg border pr-3 pl-9 text-sm transition-colors outline-none focus:ring-1"
            />
          </div>
        </div>

        {/* Language list */}
        <div className="max-h-[50vh] overflow-y-auto overscroll-contain px-2 py-2 sm:max-h-[400px]">
          {REGION_ORDER.map((region) => {
            const langs = grouped.get(region) ?? [];
            if (langs.length === 0) return null;
            return (
              <div key={region} className="mb-1">
                <div className="text-muted-foreground/50 px-2 pt-2 pb-1 text-[10px] font-bold tracking-[0.1em] uppercase">
                  {t(REGION_LABELS[region])}
                </div>
                <div className="grid grid-cols-1 gap-0.5">
                  {langs.map((lang) => {
                    const isActive = lang.code === language;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => handleSelect(lang.code)}
                        className={cn(
                          "group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-all duration-150",
                          isActive
                            ? "bg-leaf-500/10 text-leaf-400 ring-leaf-500/20 ring-1"
                            : "text-foreground/80 hover:bg-white/[0.06] active:scale-[0.98]",
                        )}
                      >
                        <span className="text-lg leading-none">
                          {lang.flag}
                        </span>
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {lang.nativeName}
                        </span>
                        <span className="text-muted-foreground/40 text-xs uppercase">
                          {lang.code}
                        </span>
                        {isActive && (
                          <Check className="text-leaf-400 h-4 w-4 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="text-muted-foreground/50 py-8 text-center text-sm">
              {t("noResults")}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Compact button version of language switcher
 */
export function LanguageSwitcherButton({
  className,
}: Omit<LanguageSwitcherProps, "compact">) {
  return <LanguageSwitcher compact className={className} />;
}
